import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import add from 'lodash/fp/add';
import flow from 'lodash/fp/flow';
import get from 'lodash/fp/get';
import isEmpty from 'lodash/fp/isEmpty';
import isUndefined from 'lodash/fp/isUndefined';
import last from 'lodash/fp/last';
import reverse from 'lodash/fp/reverse';
import sortBy from 'lodash/fp/sortBy';
import without from 'lodash/fp/without';
import { STORE_BASE_PATH } from '../../common/constants';
import type { Coords } from '../../common/types';
import type { Address, Connector } from '../facade/facadeApiV3';

export enum WaypointType {
    ADDITIONAL_STOP = 'ADDITIONAL_STOP',
    CHARGING_STATION = 'CHARGING_STATION',
    VEHICLE_LOCATION = 'vehicle-location',
}

export interface SuggestionAddress {
    title: string;
    position: Coords;
    type: string;
    resultType: string;
    station_id?: string;
}

export type Waypoint = {
    id: number;
    addressLabel?: string;
    address: Address | null;
    coordinates?: Coords;
    type: WaypointType;
    connectors?: Connector[];
    maxPower?: number;
    voltage?: number;
    current?: number;
    station_id?: string;
    timeAtStopover: string | null;
    cargoAtDeparture: number | null;
    isInRange: boolean | null;
    extended: boolean;
};

export interface FetchSuggestedAddressesActionPayload {
    id: number;
    addresses: SuggestionAddress[];
}

export interface SearchState {
    waypoints: Waypoint[];
    waypointsOrder: number[];
    isAutomatedPlanningMode: boolean;
    suggestedAddresses?: { [key: number]: SuggestionAddress[] };
    fetchSuggestedAddressesError?: string;
}

const initialState: SearchState = {
    waypoints: [
        {
            id: 100,
            addressLabel: '',
            address: null,
            type: WaypointType.ADDITIONAL_STOP,
            timeAtStopover: null,
            cargoAtDeparture: null,
            isInRange: null,
            extended: false,
        },
        {
            id: 200,
            addressLabel: '',
            address: null,
            type: WaypointType.ADDITIONAL_STOP,
            timeAtStopover: null,
            cargoAtDeparture: null,
            isInRange: null,
            extended: false,
        },
    ],
    waypointsOrder: [100, 200],
    isAutomatedPlanningMode: true,
    suggestedAddresses: undefined,
};

const getHighestId = flow(sortBy(['id']), last, get('id'), add(1));
const insertAt = (array: number[], index: number, item: number) => {
    const newArray = [...array];
    newArray.splice(index, 0, item);
    return newArray;
};
export const validWaypointPredicate = (waypoint: Waypoint): boolean =>
    !isEmpty(waypoint.addressLabel) && !isUndefined(waypoint.coordinates);

export const searchSlice = createSlice({
    name: `${STORE_BASE_PATH}search`,
    initialState,
    reducers: {
        resetWaypoints: () => initialState,
        waypointChanged: (state: SearchState, action: PayloadAction<Waypoint>) => {
            const waypointToUpdate = state.waypoints.find(waypoint => waypoint.id === action.payload.id);

            if (!waypointToUpdate) {
                return state;
            }
            state.waypoints = [...without([waypointToUpdate], state.waypoints), action.payload];
        },
        togglePlanningMode: (state: SearchState, action: PayloadAction<boolean>) => {
            state.isAutomatedPlanningMode = action.payload;
        },
        suggestedAddressesChanged: (
            state: SearchState,
            action: PayloadAction<FetchSuggestedAddressesActionPayload>
        ) => {
            const id = action.payload.id;

            state.suggestedAddresses ??= {};

            state.suggestedAddresses = {
                ...state.suggestedAddresses,
                [id]: action.payload.addresses,
            };
        },
        waypointAdded: (state: SearchState, action: PayloadAction<number>) => {
            const { waypoints, waypointsOrder } = state;

            const newId = getHighestId(waypoints);
            const newWaypoint: Waypoint = {
                id: newId,
                address: null,
                addressLabel: '',
                type: WaypointType.ADDITIONAL_STOP,
                timeAtStopover: null,
                cargoAtDeparture: null,
                isInRange: null,
                extended: false,
            };
            const position = action.payload !== -1 ? waypointsOrder.indexOf(action.payload) : waypointsOrder.length;
            const newPosition = position + 1;

            state.waypoints = [...waypoints, newWaypoint];
            state.waypointsOrder = insertAt(waypointsOrder, newPosition, newId);
        },
        waypointRemoved: (state: SearchState, action: PayloadAction<number>) => {
            const { waypoints, waypointsOrder } = state;

            if (waypoints.length <= 2) {
                return state;
            }

            state.waypoints = waypoints.filter((waypoint: Waypoint) => waypoint.id !== action.payload);
            state.waypointsOrder = waypointsOrder.filter((waypointId: number) => waypointId !== action.payload);
        },
        waypointsOrderInverted: (state: SearchState) => {
            state.waypointsOrder = reverse([...state.waypointsOrder]);
        },
        waypointsOrderChanged: (
            state: SearchState,
            action: PayloadAction<{ waypoints: Waypoint[]; newIndex: number }>
        ) => {
            const { waypoints, newIndex } = action.payload;
            state.waypoints = waypoints;
            state.waypointsOrder = waypoints.map(waypoint => waypoint.id);

            // If the waypoint is moved to the first or last spot
            // Remove extended fields and collapse waypoint
            if (newIndex === 0 || newIndex === waypoints.length - 1) {
                state.waypoints = state.waypoints.map((item, index) =>
                    index === newIndex
                        ? { ...item, timeAtStopover: null, cargoAtDeparture: null, extended: false }
                        : item
                );
            }
        },
        updateWaypointRange: (state: SearchState, action: PayloadAction<Waypoint>) => {
            const updatedItems = state.waypoints.map(waypoint =>
                waypoint.id === action.payload.id ? { ...action.payload } : waypoint
            );
            state.waypoints = updatedItems;
        },
        fetchSuggestedAddressesFailed: (state: SearchState, action: PayloadAction<string>) => {
            state.fetchSuggestedAddressesError = action.payload;
        },
        updateWaypointWaitTime: (state: SearchState, action: PayloadAction<{ waypointId: number; time: string }>) => {
            const { waypointId, time } = action.payload;
            state.waypoints = state.waypoints.map(item =>
                item.id === waypointId ? { ...item, timeAtStopover: time } : item
            );
        },
        updateWaypointCargo: (state: SearchState, action: PayloadAction<{ waypointId: number; cargo: number }>) => {
            const { waypointId, cargo } = action.payload;
            state.waypoints = state.waypoints.map(item =>
                item.id === waypointId ? { ...item, cargoAtDeparture: cargo } : item
            );
        },
        updateExtendedWaypoint: (
            state: SearchState,
            action: PayloadAction<{ waypointId: number; extended: boolean }>
        ) => {
            const { waypointId, extended } = action.payload;
            state.waypoints = state.waypoints.map(item => (item.id === waypointId ? { ...item, extended } : item));
        },
        waypointsChanged: (state: SearchState, action: PayloadAction<{ waypoints: Waypoint[] }>) => {
            const { waypoints } = action.payload;

            const validWaypoints = waypoints.filter(
                wp => wp.coordinates && (wp.coordinates.lat !== 0 || wp.coordinates.lng !== 0)
            );

            state.waypoints = validWaypoints;
            state.waypointsOrder = validWaypoints.map(waypoint => waypoint.id);
        },
    },
});

export const {
    updateExtendedWaypoint,
    resetWaypoints,
    waypointChanged,
    togglePlanningMode,
    suggestedAddressesChanged,
    waypointAdded,
    waypointRemoved,
    waypointsOrderInverted,
    waypointsOrderChanged,
    updateWaypointRange,
    fetchSuggestedAddressesFailed,
    updateWaypointWaitTime,
    updateWaypointCargo,
    waypointsChanged,
} = searchSlice.actions;

export default searchSlice.reducer;
